import type { User } from '@/types';
import { lookupDemoWorkspace } from '@/lib/auth/demoWorkspaceLookup';
import {
  hasActiveWorkspaceMembership,
  type WorkspaceRecord,
} from '@/lib/auth/workspaceAuthority';

/**
 * Runtime workspace catalog for route/membership UX mirrors.
 *
 * Demo mode continues to use the showcase catalog. Linked/staging mode
 * synthesizes an active record when the authenticated user already carries
 * membership evidence for the route workspace id (home workspace_id or an
 * active workspace_member scoped relationship). Server RLS/RPC remains
 * authoritative.
 */
const linkedWorkspaceCache = new Map<string, WorkspaceRecord>();

export function rememberLinkedWorkspace(record: WorkspaceRecord): void {
  if (!record?.id) return;
  linkedWorkspaceCache.set(record.id, {
    id: record.id,
    organizationId: record.organizationId,
    status: record.status,
  });
}

export function clearLinkedWorkspaceCache(): void {
  linkedWorkspaceCache.clear();
}

export function lookupRuntimeWorkspace(
  workspaceId: string,
  user?: Pick<User, 'account_role' | 'workspace_id' | 'status' | 'scopedRelationships'> | null,
): WorkspaceRecord | null {
  const demo = lookupDemoWorkspace(workspaceId);
  if (demo) return demo;

  const cached = linkedWorkspaceCache.get(workspaceId);
  if (cached) return cached;

  if (!user || !hasActiveWorkspaceMembership(user, workspaceId)) return null;

  const synthesized: WorkspaceRecord = {
    id: workspaceId,
    organizationId: `linked-org:${workspaceId}`,
    status: 'active',
  };
  linkedWorkspaceCache.set(workspaceId, synthesized);
  return synthesized;
}
